import { jsPDF as JSPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { VRackLogoBase64 } from '../../public/static/vrack-logo-base64';

/**
 * SHARED DATA PREPARATION
 * Groups by Part Number, filters 'Excluded - Replaced', aggregates totals,
 * and sorts by size (Small, Medium, Large, XL, Custom).
 */
const prepareExportData = (data, additionalMeta = {}) => {
  const isTeamReport = !data.billable_teams && !data.teams;
  const teamsToProcess = isTeamReport ? [data] : data.teams || data.billable_teams || [];

  // Enforce Company Name Fallback
  let companyName = '';
  const dataCompanyName = data.name || data.company_name || data.company;

  if (isTeamReport) {
    companyName = data.company || '';
  } else if (dataCompanyName && dataCompanyName.trim() !== '') {
    companyName = dataCompanyName.trim();
  } else {
    // Fallback to ID if name is missing (Supports data.id and data.company_id)
    companyName = data.id || data.company_id || '';
  }

  // Extract Metadata (Added camelCase fallbacks: invoiceNumber, billingMonth, platformFee)
  const metadata = {
    invoiceId: isTeamReport
      ? additionalMeta.invoiceId || ''
      : data.invoiceNumber || data.invoice_number || data.invoice_id || '',
    billingMonth: isTeamReport
      ? additionalMeta.billingMonth || ''
      : data.billingMonth || data.billing_month || '',
    companyName,
    projectCode: isTeamReport
      ? data.projectCode || data.project_code || ''
      : teamsToProcess[0]?.projectCode || teamsToProcess[0]?.project_code || '',
    teamName: isTeamReport ? data.teamName || data.team_name || data.name || '' : '',
    platformFee: isTeamReport ? 0 : Number(data.platformFee || data.platform_fee) || 0
  };

  // Streamlined Headers
  const headers = ['Part Number', 'Quantity', 'Billable Qty', 'Unit Price', 'Calculated Cost'];

  // Aggregate Data by Part Number
  const aggregatedParts = {};
  let totalUsageCost = 0;

  teamsToProcess.forEach((team) => {
    // Support both `billableParts` (new) and `billable_parts` (old)
    const parts = team.billableParts || team.billable_parts || [];

    parts.forEach((part) => {
      const partNum = part.part_number || 'Unknown Part';
      const unitPrice = Number(part.unit_price) || 0;

      // Filter out 'Excluded - Replaced' deployments
      const activeDeployments = (part.deployments || part.vrack_deployments || []).filter(
        (dep) => dep.billing_treatment !== 'Excluded - Replaced'
      );

      // If this part has no active deployments left, completely skip it
      if (activeDeployments.length === 0) return;

      // Sum quantities for the active deployments
      let totalQty = 0;
      let totalBillableQty = 0;
      activeDeployments.forEach((dep) => {
        totalQty += Number(dep.quantity) || 0;
        totalBillableQty += Number(dep.billable_quantity) || 0;
      });

      const cost = unitPrice * totalBillableQty;

      // Group into our dictionary
      if (!aggregatedParts[partNum]) {
        aggregatedParts[partNum] = {
          partNum,
          quantity: 0,
          billableQty: 0,
          unitPrice,
          cost: 0
        };
      }

      aggregatedParts[partNum].quantity += totalQty;
      aggregatedParts[partNum].billableQty += totalBillableQty;
      aggregatedParts[partNum].cost += cost;

      totalUsageCost += cost;
    });
  });

  // Sort by Size (S, M, L, X, C)
  const sortOrder = { S: 1, M: 2, L: 3, X: 4, C: 5 };

  const sortedParts = Object.values(aggregatedParts).sort((a, b) => {
    const sizeA = a.partNum.split('-').pop();
    const sizeB = b.partNum.split('-').pop();
    const orderA = sortOrder[sizeA] || 99;
    const orderB = sortOrder[sizeB] || 99;
    return orderA - orderB;
  });

  // Convert to rows array
  const rows = sortedParts.map((p) => [p.partNum, p.quantity, p.billableQty, p.unitPrice, p.cost]);

  return {
    metadata,
    headers,
    rows,
    totalUsageCost,
    platformFee: metadata.platformFee,
    grandTotal: totalUsageCost + metadata.platformFee,
    isTeamReport
  };
};

/**
 * SHARED CSV EXPORT
 */
export const exportDeploymentsCsv = (data, additionalMeta = {}) => {
  const { metadata, headers, rows, totalUsageCost, platformFee, grandTotal, isTeamReport } =
    prepareExportData(data, additionalMeta);
  const csvRows = [];

  const escapeCsv = (str) => String(str || '').replace(/"/g, '""');

  // Metadata Block
  if (metadata.invoiceId) csvRows.push(`"Invoice ID:","${escapeCsv(metadata.invoiceId)}"`);
  if (isTeamReport) csvRows.push(`"Team Name:","${escapeCsv(metadata.teamName)}"`);
  csvRows.push(`"Company Name:","${escapeCsv(metadata.companyName)}"`);
  if (metadata.billingMonth) csvRows.push(`"Billing Month:","${escapeCsv(metadata.billingMonth)}"`);
  if (metadata.projectCode) csvRows.push(`"Project Code:","${escapeCsv(metadata.projectCode)}"`);
  csvRows.push('');

  // Table Headers
  csvRows.push(headers.map((h) => `"${h}"`).join(','));

  // Table Data
  rows.forEach((row) => {
    csvRows.push(row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(','));
  });

  // Summary Block
  csvRows.push('');
  const costIndex = headers.indexOf('Calculated Cost');
  const createCsvSummaryRow = (label, value) => {
    const row = Array(headers.length).fill('""');
    row[0] = `"${label}"`;
    if (costIndex !== -1) row[costIndex] = `"${value}"`;
    return row.join(',');
  };

  if (isTeamReport) {
    csvRows.push(createCsvSummaryRow('TOTAL TEAM COST', totalUsageCost));
  } else {
    csvRows.push(createCsvSummaryRow('SUBTOTAL (LRU Usage)', totalUsageCost));
    csvRows.push(createCsvSummaryRow('PLATFORM FEE', platformFee));
    csvRows.push(createCsvSummaryRow('TOTAL INVOICE COST', grandTotal));
  }

  // Trigger Download
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const filePrefix = isTeamReport ? metadata.teamName : metadata.companyName;
  const safePrefix = filePrefix.replace(/\s+/g, '_');
  const dateSuffix = metadata.billingMonth ? `_${metadata.billingMonth}` : '';

  link.download = `${safePrefix}${dateSuffix}_billing_report.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * SHARED PDF EXPORT
 */
export const exportDeploymentsPdf = (data, additionalMeta = {}) => {
  const { metadata, headers, rows, totalUsageCost, platformFee, grandTotal, isTeamReport } =
    prepareExportData(data, additionalMeta);
  const doc = new JSPDF({ orientation: 'landscape' });

  // Add Logo
  doc.addImage(VRackLogoBase64, 'PNG', 14, 10, 40, 12);

  // Metadata Text
  doc.setFontSize(16);
  doc.text(isTeamReport ? 'Team Billing Report' : 'Company Billing Report', 14, 32);
  doc.setFontSize(10);
  doc.setTextColor(100);

  if (isTeamReport) {
    doc.text(`Team Name: ${metadata.teamName}`, 14, 40);
    doc.text(`Company Name: ${metadata.companyName}`, 14, 45);
    if (metadata.projectCode) doc.text(`Project Code: ${metadata.projectCode}`, 14, 50);
    if (metadata.invoiceId) doc.text(`Invoice ID: ${metadata.invoiceId}`, 100, 40);
    if (metadata.billingMonth) doc.text(`Billing Month: ${metadata.billingMonth}`, 100, 45);
  } else {
    doc.text(`Invoice ID: ${metadata.invoiceId}`, 14, 40);
    doc.text(`Company Name: ${metadata.companyName}`, 14, 45);
    doc.text(`Billing Month: ${metadata.billingMonth}`, 14, 50);
    if (metadata.projectCode) doc.text(`Project Code: ${metadata.projectCode}`, 100, 40);
  }

  // Format numbers as Currency
  const formattedRows = rows.map((row) => {
    const newRow = [...row];
    newRow[3] = `$${Number(newRow[3]).toFixed(2)}`; // Unit Price is now index 3
    newRow[4] = `$${Number(newRow[4]).toFixed(2)}`; // Cost is now index 4
    return newRow;
  });

  const createPdfSummaryRow = (label, value) => {
    const row = Array(headers.length).fill('');
    row[0] = label;
    row[4] = `$${value.toFixed(2)}`; // Cost is now index 4
    return row;
  };

  let summaryRowCount = 0;
  if (isTeamReport) {
    formattedRows.push(createPdfSummaryRow('TOTAL TEAM COST', totalUsageCost));
    summaryRowCount = 1;
  } else {
    formattedRows.push(createPdfSummaryRow('SUBTOTAL (LRU Usage)', totalUsageCost));
    formattedRows.push(createPdfSummaryRow('PLATFORM FEE', platformFee));
    formattedRows.push(createPdfSummaryRow('TOTAL INVOICE COST', grandTotal));
    summaryRowCount = 3;
  }

  autoTable(doc, {
    startY: 60,
    head: [headers],
    body: formattedRows,
    theme: 'grid',
    headStyles: { fillColor: [44, 62, 80] },
    styles: { fontSize: 8, cellPadding: 2 },
    didParseCell(cellData) {
      // Highlight summary rows
      if (
        cellData.section === 'body' &&
        cellData.row.index >= formattedRows.length - summaryRowCount
      ) {
        cellData.cell.styles.fontStyle = 'bold';
        cellData.cell.styles.fillColor = [245, 245, 245];
      }
    }
  });

  const filePrefix = isTeamReport ? metadata.teamName : metadata.companyName;
  const safePrefix = filePrefix.replace(/\s+/g, '_');
  const dateSuffix = metadata.billingMonth ? `_${metadata.billingMonth}` : '';

  doc.save(`${safePrefix}${dateSuffix}_billing_report.pdf`);
};
